// Bring-your-own-song pathfinding — the one new algorithm for the on-ramp. Given the
// curriculum lattice and a target node, find the shortest route (fewest rungs) from a
// curriculum **entry** (a graph source — the root the F11 guard guarantees) to the
// target: the ordered drills + apply pieces to practise. Pure, unit-tested. Mastery is
// applied by the caller for display (which rungs are already done) — the path itself is
// the full chain.

import type { LatticeNode, SongLattice } from "./song-lattice";

/**
 * Shortest path (fewest rungs) from a lattice entry (a node with no incoming edge) to
 * `targetId`, inclusive, over forward edges. Returns the node sequence to climb, or
 * `null` if the target is unknown or no entry reaches it.
 */
export function pathTo(lattice: SongLattice, targetId: string): LatticeNode[] | null {
  const byId = new Map(lattice.nodes.map((n) => [n.id, n]));
  if (!byId.has(targetId)) return null;

  const hasIncoming = new Set(lattice.edges.map((e) => e.to));
  const adj = new Map<string, string[]>();
  for (const e of lattice.edges) {
    const list = adj.get(e.from);
    if (list) list.push(e.to);
    else adj.set(e.from, [e.to]);
  }

  // BFS from every entry (source) node; `prev` doubles as the visited set.
  const prev = new Map<string, string | null>();
  const queue: string[] = [];
  for (const n of lattice.nodes) {
    if (!hasIncoming.has(n.id)) {
      prev.set(n.id, null);
      queue.push(n.id);
    }
  }
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    for (const next of adj.get(cur) ?? []) {
      if (prev.has(next)) continue;
      prev.set(next, cur);
      queue.push(next);
    }
  }
  if (!prev.has(targetId)) return null;

  const path: LatticeNode[] = [];
  let id: string | null = targetId;
  while (id !== null) {
    const node = byId.get(id);
    if (node) path.unshift(node);
    id = prev.get(id) ?? null;
  }
  return path;
}

/**
 * Trim a path so it starts at the learner's mastery frontier — the deepest node whose
 * requirements are all mastered — keeping that node and everything ahead. The "zoomed"
 * path view: show where you are and what's left, not the whole climb from the root.
 */
export function fromFrontier(
  path: readonly LatticeNode[],
  mastered: ReadonlySet<string>,
): LatticeNode[] {
  let start = 0;
  for (let i = 0; i < path.length; i++) {
    if (path[i]?.requirements.every((r) => mastered.has(r))) start = i;
  }
  return path.slice(start);
}
